import React, { useState, useEffect, useRef } from 'react';
import { GetRequest } from 'components/common/index.js';
import Constants from 'components/show_drawing/Constants.jsx';
import ValidationRule from 'components/show_drawing/ValidationRule.jsx';
import ComboErrorsBanner from 'components/show_drawing/ComboErrorsBanner.jsx';
import ComboBrokenRulesBanner from 'components/show_drawing/ComboBrokenRulesBanner.jsx';
import styles from 'components/show_drawing/redraw_form/_style.module.scss';
import MyEventListener from 'components/common/MyEventListener.jsx'
import BotChallenge from 'components/common/BotChallenge.jsx'

const DrawingSvg = ( { id, drawing, unit, theme, setTheme, redrawRequested, setRedrawRequested, formRef, optionsUserChanged, redrawInProgress, setRedrawInProgress, debug, GetRequestPath, setCurrentDrawingOptions, setCurrentUserDrawingOptions, setProposedDrawingOptions, advancedOptionsVisible, setResetEnabled, optionsColRef, setDownloadsDisabled, dimensionTextschecked, resetEnabled} ) => {
  const key = () => { return `${Object.values(drawing).join}` }
  const minHeight = Constants.SVG_minHeight;
  const [height, setHeight] = useState(0);
  const [svg, setSvg] = useState("");
  const [isPreview, setIsPreview] = useState(true);
  const [rules, setRules] = useState([]);
  const [logInRequired, setLogInRequired] = useState(false);
  const [hasRecommendations, setHasRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [comboErrors, setComboErrors] = useState([]);
  const [comboBrokenRules, setComboBrokenRules] = useState([]);
  // Set to the reCAPTCHA site key when the server challenges a level-2 suspected bot; the
  // challenge then renders in the canvas in place of the dieline until it is solved.
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null);
  const ValidationRuleRef = useRef();
  const redrawDebounceTimer = useRef();
  const svgLoadTimerRef = useRef(null);

  const performRedraw = (recaptchaToken) => {
    const requestPath = GetRequestPath();

    if(requestPath == "") {
      setTimeout(() => performRedraw(recaptchaToken), 250)
      return
    }

    // Start timing the SVG loading process
    svgLoadTimerRef.current = performance.now();
    resetAbcTimer();

    let url = `/svg/${requestPath}`;
    // A level-2 suspected bot must carry a fresh reCAPTCHA token (obtained from the popup
    // shown on the 403 below) or the server challenges the request again.
    if(recaptchaToken) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}g-recaptcha-response=${encodeURIComponent(recaptchaToken)}`;
    }

    GetRequest({
      url: url,
      callback: onRedrawSuccess,
      400: onBadRequest,
      403: onRecaptchaRequired,
      500: onError
    })
  }

  // The server flagged this account as a level-2 suspected bot and answered with a
  // reCAPTCHA challenge instead of the dieline. Show the challenge in the canvas (in place
  // of the dieline) and stop the spinner. Guard on recaptcha_required so a 403 that is NOT
  // this challenge (e.g. the blacklist not_robot page, which arrives as non-JSON so data
  // is false) simply clears the spinner instead of rendering a challenge.
  const onRecaptchaRequired = (data) => {
    setRedrawInProgress(() => false);
    if(!data || !data.recaptcha_required) {
      return;
    }

    setRecaptchaSiteKey(() => data.recaptcha_site_key);
  }

  // The user solved the in-canvas reCAPTCHA: remove it, show the spinner, and redraw
  // carrying the token so the server serves the dieline this time.
  const onChallengeVerified = (token) => {
    setRecaptchaSiteKey(() => null);
    setRedrawInProgress(() => true);
    performRedraw(token);
  }

  const redraw = () => {
    if(!redrawRequested) { return; }

    // Clear any existing debounce timer
    if(redrawDebounceTimer.current) {
      clearTimeout(redrawDebounceTimer.current);
    }

    // Show progress immediately for better UX
    setRedrawInProgress( () => true )

    // Set new debounce timer for 100ms
    redrawDebounceTimer.current = setTimeout(() => {
      // Check again in case redrawRequested became false
      if(!redrawRequested) {
        setRedrawInProgress( () => false );
        return;
      }
      performRedraw();
      redrawDebounceTimer.current = null;
    }, 100);
  }
  const fetchDrawingOptionValues = () => {
    GetRequest({
      url: `/drawing_option_values_json/${GetRequestPath()}`,
      callback: (data) => {
        data.map(processDrawingOptionResult)
      }
    })
  }

  const processDrawingOptionResult = (option) => {
    emitEvent(Constants.drawing_option_recommended_value_changed, {
      id: option.drawing_id,
      name: option.name,
      value: option.value,
      disabled: option.disabled,
      visible: option.visible,
      user_changed: option.user_changed,
      auto_calculated: option.auto_calculated
    })
  }

  const storeInLocalStorage = (formula_options) => {
    if (!isLocalStorageSupported()) { return }
    let keyForLocalStorage = `fo_${drawing.main_id}_${unit}`;
    let selectedKeys = formula_options["options_user_changed"].split(",");

    selectedKeys = selectedKeys.concat( ["material_id", "malzeme", "printing_method", "add_ons"] )

    // Include all part_material_* and custom_part_material_value_* fields
    const partMaterialKeys = Object.keys(formula_options).filter(key =>
      key.startsWith("part_material_") || key.startsWith("custom_part_material_value_")
    );
    selectedKeys = selectedKeys.concat(partMaterialKeys);

    if(drawing.show_internal_dimensions && formula_options["internal_dimensions"]) { selectedKeys = selectedKeys.concat( ["internal_dimensions"] ) }

    if (formula_options["add_ons"] != undefined && formula_options["add_ons"] != '' ){
      for(let i = 0; i< formula_options["add_ons"].length; i++) {
        selectedKeys = selectedKeys.concat( drawing.add_on_option_names[ formula_options["add_ons"][i] ] )
      }
    }

    const toStore = selectedKeys.reduce((acc, key) => {
      if (formula_options.hasOwnProperty(key)) {
        acc[key] = formula_options[key];
      }
      return acc;
    }, {});

    toStore.advancedOptionsVisible = advancedOptionsVisible;

    // Persist combo guest inputs (ask_values[...] and combo_materials[...]) so a
    // refresh rebuilds the drawing with the user's last separator/etc. These keys
    // are intentionally NOT permitted by FormulaOptionActions#formula_parameters
    // (see app/controllers/concerns/formula_option_actions.rb), so they never
    // appear in user_formula_options; read them from the form snapshot captured
    // at request time instead.
    const requestPathData = window.GetRequestPathData || {};
    Object.keys(requestPathData).forEach((pathKey) => {
      if (pathKey.startsWith('ask_values[') || pathKey.startsWith('combo_materials[')) {
        toStore[pathKey] = requestPathData[pathKey];
      }
    });

    localStorage.setItem(keyForLocalStorage, JSON.stringify(toStore));
    emitEvent('addDebugEntry', { event: "writingLocalStore", user_options: toStore })

    setResetEnabled( () => true );
  }

  const emitDoneMessage = () => {
    emitEvent('addDebugEntry', { event: "[DrawingSvg] setSvg", id: id })
    emitEvent(Constants.drawing_svg_updated, { id: id })
  }

  const onRedrawSuccess = (data) => {
    // Calculate and log the total time taken
    if (svgLoadTimerRef.current !== null) {
      const duration = performance.now() - svgLoadTimerRef.current;
      console.log(`SVG load completed in ${duration.toFixed(2)}ms`);
      svgLoadTimerRef.current = null;
    }

    emitEvent('addDebugEntry', {
      event: "onRedrawSuccess",
      _from: window.GetRequestPathData,
      _to: data.formula_options,
      user_formula_options: data.user_formula_options
       })

    if(data.preview != undefined) {
      if(data.preview) {
        setSvg( () => data.svg)

        //super edge-case, where we need to redraw twice because of add-ons in user-download or localstorage and first one was denied but this one was OK.
        noPreviewPromoHide()
        setTimeout(noPreviewPromoHide, 100)
        setTimeout(noPreviewPromoHide, 500)

        if(data.past_due_payment && data.past_due_payment.past_due) {
          document.dispatchEvent(new CustomEvent('show_drawing.payment_past_due', { detail: data.past_due_payment }));
        }
      } else {
        noPreviewPromoShow(data)
      }

      window.currentDrawingOptions = data.formula_options; // legacy
      setCurrentDrawingOptions( () => data.formula_options)
      setCurrentUserDrawingOptions( () => data.user_formula_options)
      storeInLocalStorage( data.user_formula_options );
      setDownloadsDisabled( () => false );
      setIsPreview(() => data.preview)
    }

    fetchDrawingOptionValues();
    setRedrawInProgress( () => false )
    setRedrawRequested( () => false );
    setRules( () => [ ] )
    setHasRecommendations( () => data.has_recommendations )
    setRecommendations( () => data.recommendations )
    setComboErrors( () => data.combo_errors || [] )
    setComboBrokenRules( () => data.combo_broken_rules || [] )

    // Enable menu tabs before checking pending navigation
    enableMenuTabs();

    // Handle pending navigation from tab click (validates before tab navigation)
    // This must happen BEFORE emitting drawing_redraw_completed to prevent jQuery listener race condition
    if (window.pendingMenuTabNavigation) {
      // Defensive check - if menu tabs got disabled by another code path, cancel navigation
      if (window.menu_tabs_disabled) {
        if (window.pendingMenuTabNavigation.newTabWindow) {
          window.pendingMenuTabNavigation.newTabWindow.close();
        }
        window.pendingMenuTabNavigation = null;
        return;
      }

      const pending = window.pendingMenuTabNavigation;
      window.pendingMenuTabNavigation = null;

      if (pending.newTabWindow) {
        // The user opened the 3D mockup in a new tab from the dieline page (the
        // blank tab was opened during the click so the popup blocker allowed it).
        // Now that the redraw has validated the options, point that tab at the 3D
        // URL and stay on the dieline page so both stay open side by side. Build
        // the params from a snapshot of the form instead of stripping the
        // authenticity_token/file_name/download_notes inputs off this still-live
        // page the way the same-tab branch below does.
        const downloadForm = document.getElementById('download_form_dxf');
        let encodedArguments;
        if (!downloadForm) {
          encodedArguments = btoa(window.get_request_path?.().split("?")[1] || '');
        } else {
          const params = new URLSearchParams(new FormData(downloadForm));
          ['authenticity_token', 'file_name', 'download_notes'].forEach((suffix) => {
            [...params.keys()]
              .filter((key) => key.endsWith(suffix))
              .forEach((key) => params.delete(key));
          });
          encodedArguments = btoa(params.toString());
        }
        pending.newTabWindow.location.href = pending.action + '/' + encodedArguments + `?advanced_options_enabled=${pending.advancedOptionsEnabled}`;
        // Fall through to the normal redraw completion below so the dieline page settles.
      } else {
        // Navigate with confirmed values in the same tab. This page is leaving, so
        // it is fine to strip the inputs we do not want encoded into the URL.
        const authTokens = document.querySelectorAll('input[name$="authenticity_token"]');
        const fileNames = document.querySelectorAll('input[name$="file_name"]');
        const downloadNotes = document.querySelectorAll('input[name$="download_notes"]');

        authTokens.forEach(el => el.remove());
        fileNames.forEach(el => el.remove());
        downloadNotes.forEach(el => el.remove());

        const downloadForm = document.getElementById('download_form_dxf');

        if (!downloadForm) {
          const requestPath = window.get_request_path?.().split("?")[1];
          window.location.href = pending.action + '/' + btoa(requestPath || '') + `?advanced_options_enabled=${pending.advancedOptionsEnabled}`;
        } else {
          const formData = new FormData(downloadForm);
          const params = new URLSearchParams(formData).toString();
          window.location.href = pending.action + '/' + btoa(params) + `?advanced_options_enabled=${pending.advancedOptionsEnabled}`;
        }
        // Navigation triggered - page will redirect, no need to continue
        return;
      }
    }

    emitEvent(Constants.drawing_redraw_completed, { id: id })

    // sending DONE signal many times so LoadFromLocalStorage can clean up properly
    emitDoneMessage()
    setTimeout(emitDoneMessage, 10)
    setTimeout(emitDoneMessage, 100)
    setTimeout(emitDoneMessage, 250)
    setTimeout(emitDoneMessage, 500)
  }

  const onBadRequest = (data) => {
    // Reset timer on validation error
    svgLoadTimerRef.current = null;

    // Clear any pending navigation since validation failed; close the 3D tab we
    // opened optimistically for a new-tab navigation.
    if (window.pendingMenuTabNavigation?.newTabWindow) {
      window.pendingMenuTabNavigation.newTabWindow.close();
    }
    window.pendingMenuTabNavigation = null;

    disableMenuTabs();
    if(data.rules.length > 0) {
      emitEvent(Constants.drawing_redraw_completed, { id: id })
      setRules( () => data.rules )
      setLogInRequired( () => data.log_in_required )
      setHasRecommendations( () => data.has_recommendations )
      setRecommendations( () => data.recommendations )
      setDownloadsDisabled( () => true );
      if(data.rules_and_formula_options_match != undefined && !data.rules_and_formula_options_match) {
        emitEvent(Constants.drawing_rules_and_formula_options_do_not_match, { id: id })
      }
    }

    setRedrawRequested(  () => false );
    setRedrawInProgress( () => false )
  }

  const onError = (data) => {
    // Reset timer on error
    svgLoadTimerRef.current = null;

    // Clear any pending navigation since request failed; close the 3D tab we
    // opened optimistically for a new-tab navigation.
    if (window.pendingMenuTabNavigation?.newTabWindow) {
      window.pendingMenuTabNavigation.newTabWindow.close();
    }
    window.pendingMenuTabNavigation = null;

    trackEvent('js_redraw_error_triggers_form_reset', {
      drawing_id: id,
      path: window.location.pathname,
      rails_env: window.RailsEnv,
    });

    if (window.RailsEnv === 'development') { return; }

    emitEvent('reset_requested', {})
  }

  const hasRulesOrRecommendations = () => {
    return rules.length > 0 || (recommendations != undefined && recommendations.length > 0);
  }

  const resizeSvg = () => {
    if( $("#svg").length == 0 ) { setTimeout(resizeSvg, 250); return }
    var h = parseInt($(optionsColRef.current).height())
    var panZoomValue = 0.95;
    var panZoomInstance = getSvgPanZoom('#svg', panZoomValue);

    if(h < minHeight) { h = minHeight }

    h = h + "px"
    setHeight( () => h );
    $("#svg").height( h );
    panZoomInstance.resize();
    panZoomInstance.fit();
    panZoomInstance.center();
    panZoomInstance.zoom(panZoomValue);
   }

  const getSVGUrl = () => {
    let mapping = {
      black_bg: {
        in: drawing.image.svg_in_url,
        infr: drawing.image.svg_infr_url,
        mm: drawing.image.svg_url,
      },
      white_bg: {
        in: drawing.image.svg_in_white_bg_url,
        infr: drawing.image.svg_infr_white_bg_url,
        mm: drawing.image.svg_white_bg_url,
      },
      gray_bg: {
        in: drawing.image.svg_in_gray_bg_url,
        infr: drawing.image.svg_infr_gray_bg_url,
        mm: drawing.image.svg_gray_bg_url,
      }
    }
    return mapping[theme][unit]
  }

  const svgStatus = (res) => {
    if (res.status >= 200 && res.status < 300) {
      return Promise.resolve(res)
    } else {
      setRedrawRequested( () => true );

      trackEvent('svg_fetch_failed', {
        url: getSVGUrl(),
        id: id
      });
      return Promise.reject(new Error(res.statusText))
    }
  }

  const showDefaultSvg = () => {
    if(drawing.image == undefined) { return }
    var url = getSVGUrl();
    fetch(url)
    .then( svgStatus )
    .then( (response) => response.text() )
    .then(function(data) {
      setSvg( () => data );
    }).catch(function(error) {
    // console.log({error});
    });
  }

  const onOptionsUpdated = (event) => {
    if(event.detail == id) {
     resizeSvg();
    }
  }

  const timer = useRef();

  const showAbcLine = (e) => {
    const targetId = "abc-" + e.detail.id + "-" + e.detail.name;

    $( ".dmt").fadeTo( "slow" , 0);
    $( "#" + targetId).fadeTo( "slow" , 1);
    $.each($('.abc'), function() {
      if(this.id != targetId) {
        $( "#" + this.id).fadeTo( "slow" , 0);
      }
    });

    if(timer.current != undefined) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(function(){
      if( dimensionTextschecked ) { $( ".dmt").fadeTo( "slow" , 1); }
      $( ".abc").fadeTo( "slow" , 0);
    }, 5000);

  }

  const resetAbcTimer = () => {
    // Clear timer and immediately hide abc-lines, show dimension texts
    if(timer.current != undefined) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }

    // Immediately show dimension texts and hide abc-lines
    if( dimensionTextschecked ) { $( ".dmt").fadeTo( "fast" , 1); }
    $( ".abc").fadeTo( "fast" , 0);
  }

  // useEffect starts.....................................................................
  useEffect( showDefaultSvg, [id, drawing, theme, unit]);
  useEffect( redraw, [redrawRequested] );

  useEffect(() => {
    setTimeout(resizeSvg, 250);
  }, [svg, resetEnabled] );

  useEffect(() => {
    window.svg_initial_stroke = null;
    resetAbcTimer();
  }, [svg] );

  useEffect( () => {
    if(svg == "") { return }
    resizeSvg()
  }, [svg, rules, recommendations, optionsColRef.current])

  useEffect( () => { dimensionTextschecked ? $(".dmt").show() : $(".dmt").hide() }, [svg, dimensionTextschecked])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if(redrawDebounceTimer.current) {
        clearTimeout(redrawDebounceTimer.current);
      }
    };
  }, []);

  // useEffect ends

  MyEventListener([
    {
      key: Constants.options_updated,
      callback: onOptionsUpdated
    },{
      key: Constants.show_abc_line,
      callback: showAbcLine
    }
  ])

  return (
    <div key={key()}>
      {debug && <div> [{id}] [{height}] [{unit}]<br />{debug} </div> }

      {hasRulesOrRecommendations() && <ValidationRule myRef={ValidationRuleRef} id={id} rules={rules} logInRequired={logInRequired} recommendations={recommendations} />}

      <ComboErrorsBanner errors={comboErrors} />
      <ComboBrokenRulesBanner id={id} comboBrokenRules={comboBrokenRules} />

      <div id="mainDrawingContainer" style={{ height: height }} className={`${(redrawInProgress) ? styles.svg_in_progress : 'redraw_theme_' + theme } ${isPreview ? '' : styles.svg_is_not_preview  }`}>
        {recaptchaSiteKey
          ? <BotChallenge siteKey={recaptchaSiteKey} onVerified={onChallengeVerified} />
          : <div dangerouslySetInnerHTML={{__html: svg}}></div>}
      </div>

    </div>
  )
}

export default DrawingSvg;
